import { CSSProperties } from 'react';
import classNames from 'classnames';
import { differenceInDays, eachDayOfInterval, isSameDay } from 'date-fns';
import styled from 'styled-components';

import type { Absence as AbsenceType } from '../types';
import { Interval } from 'components/types';

import { fromISOString } from 'utils/date';
import { brightness } from 'utils/misc';

import { Tooltip } from 'components/shared';

import getTooltipContent from '../../AbsenceTooltipContent';
import { Icon } from '@sdb/planning-ui';

const AbsenceStyles = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  margin-bottom: 2px;
  cursor: default;
  border: 1px solid;
  border-radius: 3px;

  &.is-full-block {
    position: absolute;
    left: 0px;
    top: 2px;
    bottom: 2px;
    z-index: 3;
    box-sizing: border-box;
  }

  &.is-multiday-absence {
    &.is-leave-request,
    &.is-unknown-request {
      .absence {
        opacity: 0.5;
      }
    }
  }

  &:not(.is-multiday-absence) {
    &.is-leave-request,
    &.is-unknown-request {
      opacity: 0.5;
    }
  }

  &.is-external-illness {
    background-size: 25px 25px !important;
  }

  &.is-multiday-absence:not(.is-full-block) {
    border: 0;
    background: none;
    position: relative;
  }

  &.has-left-arrow {
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  &.has-right-arrow {
    border-right: none;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    z-index: 4;

    &.left {
      left: 2px;
    }
    &.right {
      right: 2px;
    }
  }
`;

type Props = {
  absence: AbsenceType;
  date: Date;
  interval: Interval;
  className?: string;
  style?: CSSProperties;
  isFullBlock?: boolean;
};

type AbsenceBlockProps = {
  absence: AbsenceType;
  date: Date;
  startDate: Date;
  endDate: Date;
  absenceLength: number;
  interval: Interval;
};

export default function Absence({
  absence,
  date,
  interval,
  className,
  style,
  isFullBlock
}: Props) {
  const startDate = fromISOString(absence.start);
  const endDate = fromISOString(absence.end);
  const isLeave = absence.type === 'LEAVE';
  const isIllness = absence.type === 'ILLNESS';
  const { dutyHourType } = absence;
  const dhtColor =
    dutyHourType != null && dutyHourType.colorCode != null
      ? (dutyHourType.colorCode as `#${string}`)
      : isLeave
      ? '#33ff99'
      : isIllness
      ? '#ff0033'
      : '#dedede';
  let background = 'white';
  const isLeaveRequest = isLeave && !absence.isApproved;
  const isExternalIllness = isIllness && absence.isExternal;
  const isUnknownRequest =
    !isLeave && !isIllness && (!absence.isApproved || absence.isExternal);

  if (isExternalIllness) {
    const stripeColor = brightness(dhtColor, 85);
    background = `
            linear-gradient(
                -45deg,
                ${stripeColor} 25%,
                #ffffff 25%,
                #ffffff 50%,
                ${stripeColor} 50%,
                ${stripeColor} 75%,
                #ffffff 75%,
                #ffffff 100%
            )
        `;
  }
  const endTime = new Date(endDate.getTime() - 1000);
  const absenceLength = eachDayOfInterval({
    start: startDate,
    end: new Date(endDate.getTime() - 1000)
  }).length;
  const isMultiDayAbsence = absenceLength > 1;
  const hasLeftArrow = isFullBlock && startDate < interval.startDate;
  const hasRightArrow = isFullBlock && endTime > interval.endDate;
  const hasAbsenceBlockStart =
    absenceLength > 1 &&
    (isSameDay(date, startDate) ||
      (isSameDay(date, interval.startDate) && startDate < interval.startDate));

  const absenceContent = (
    <AbsenceStyles
      className={classNames(className, 'absence', {
        'is-leave': isLeave,
        'is-illness': isIllness,
        'is-unknown': !isLeave && !isIllness,
        'is-unknown-request': isUnknownRequest,
        'is-external-illness': isExternalIllness,
        'is-leave-request': isLeaveRequest,
        'is-multiday-absence': isMultiDayAbsence,
        'has-left-arrow': hasLeftArrow,
        'has-right-arrow': hasRightArrow
      })}
      style={{
        ...(!isMultiDayAbsence || isFullBlock
          ? {
              borderColor: dhtColor,
              background
            }
          : {}),
        ...(style || {})
      }}
    >
      {hasAbsenceBlockStart && !isFullBlock && (
        <AbsenceFullBlock
          absence={absence}
          date={date}
          startDate={startDate}
          endDate={endTime}
          absenceLength={absenceLength}
          interval={interval}
        />
      )}
      {hasLeftArrow && (
        <Icon type="arrow-left" fontSize="small" className="arrow left" />
      )}
      {(!isMultiDayAbsence || isFullBlock) && (
        <Icon
          type={
            isLeave ? 'tree-palm' : isIllness ? 'house-chimney-medical' : 'lock'
          }
          fontSize="small"
          style={
            isIllness && absence.externalAbsenceMismatch
              ? { color: dhtColor }
              : undefined
          }
        />
      )}
      {hasRightArrow && (
        <Icon type="arrow-right" fontSize="small" className="arrow right" />
      )}
    </AbsenceStyles>
  );

  if (isMultiDayAbsence && !isFullBlock) {
    return absenceContent;
  }

  return (
    <Tooltip
      title={getTooltipContent({
        absence,
        showDate: true,
        showTypeLabel: true
      })}
      arrow
      enterDelay={300}
      enterNextDelay={300}
      placement="right"
      disableInteractive
    >
      {absenceContent}
    </Tooltip>
  );
}

const AbsenceFullBlock = ({
  absence,
  date,
  startDate,
  endDate,
  absenceLength,
  interval
}: AbsenceBlockProps) => {
  const blockLength =
    absenceLength -
    differenceInDays(date, startDate) -
    (endDate > interval.endDate
      ? differenceInDays(endDate, interval.endDate) + 1
      : 0);

  return (
    <Absence
      absence={absence}
      date={date}
      interval={interval}
      className="is-full-block"
      style={{
        width: `calc(${blockLength * 100}% + ${(blockLength - 1) * 9}px)`
      }} // 4px left, 4px right, 1px border
      isFullBlock
    />
  );
};
